import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import { useModalLayer } from '../../lib/modalStack';

export interface SearchableOption {
  value: string;
  label: string;
  /** Optional role tag rendered next to the label, e.g. "Admin", "Employer". */
  tag?: string;
  /** Extra hint shown under/next to the label (e.g. email). */
  hint?: string;
  /** Rendered in the list but not selectable (e.g. already-invited jobs). */
  disabled?: boolean;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  emptyLabel?: string;
  /** Show a "None / clear" option at the top (default true). */
  allowClear?: boolean;
  clearLabel?: string;
  /** Disable the whole control (input cannot be opened or edited). */
  disabled?: boolean;
  ariaLabel?: string;
  id?: string;
  /**
   * Position of the dropdown menu relative to the input element:
   * 'bottom': always drops down
   * 'top': always drops up above the input
   * 'auto': (default) detects available viewport space at open time and
   * drops up only when the space below is tight and there is more above
   */
  dropdownPosition?: 'bottom' | 'top' | 'auto';
}

/** Height cap of the option list (the old Tailwind `max-h-56` = 14rem). */
const MENU_MAX_HEIGHT = 224;
/** Gap between the input and the list (the old `mt-1` / `mb-1`). */
const MENU_GAP = 4;
/** Smallest distance the portalled list keeps from the viewport edges. */
const VIEWPORT_MARGIN = 8;

interface MenuPosition {
  direction: 'bottom' | 'top';
  /** Fixed `top` coordinate (direction 'bottom'); unset when flipped up. */
  top?: number;
  /** Fixed `bottom` coordinate (direction 'top'); unset when dropping down. */
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

/**
 * Searchable single-select combobox. Filters options by typing, shows an
 * optional role tag per option, and allows clearing the selection.
 *
 * SYS-10: the option list is PORTALLED to <body> and positioned `fixed`
 * from the input's bounding rect. It used to be absolutely positioned
 * inside this component's relative wrapper, where any ancestor with
 * overflow hidden/auto (`.card`, table scrollers, modal panels) clipped
 * it at the container edge — no z-index can rescue an overflow clip.
 * The position is recomputed on open, on window resize and on ANY scroll
 * (capture phase, so inner scroll containers count); if the input is
 * scrolled fully out of the viewport the list closes instead of floating
 * detached from its field. Layering comes from the shared modal stack
 * (`useModalLayer`): the list opens after any modal that hosts it, so it
 * registers a later layer and always paints above that modal.
 *
 * Batch R2 (SYS follow-up to REF-04/REF-07): on touch widths the input
 * renders at text-base (16px — below that iOS Safari auto-zooms on
 * focus) with py-2.5, and the option/clear rows carry a 44px minimum
 * hit height; at md and up both restore the original compact desktop
 * sizing exactly (py-2 / text-sm input, natural row height).
 */
export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  emptyLabel = 'No options found',
  allowClear = true,
  clearLabel = '— None —',
  disabled = false,
  ariaLabel,
  id,
  dropdownPosition = 'auto',
}: SearchableSelectProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // True when the most recent mousedown landed inside the portalled list.
  // Such a mousedown also blurs the input; that blur must not close the
  // list (the pointer is interacting with it, not leaving the control).
  const menuPointerRef = useRef(false);

  // Register with the global modal stack while open so the list paints
  // above any popup that hosts this select (stack base is --z-modal 9000
  // and every layer opened later sits higher). While the list is the
  // topmost layer, Escape closes the list; a second Escape then reaches
  // the popup below — the stack's nested-popup semantics.
  const menuZ = useModalLayer(open && !disabled, () => setOpen(false));

  const selected = options.find(o => o.value === value);
  const filtered = options.filter(o =>
    `${o.label} ${o.hint || ''} ${o.tag || ''}`.toLowerCase().includes(query.trim().toLowerCase())
  );

  /**
   * Compute the list's fixed position from the input wrapper's rect.
   * Returns null when the input is fully outside the viewport (the list
   * has nothing to anchor to — callers close instead).
   */
  const computeMenuPosition = useCallback((): MenuPosition | null => {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (rect.bottom < 0 || rect.top > vh || rect.right < 0 || rect.left > vw) return null;

    const spaceBelow = vh - rect.bottom;
    let direction: 'bottom' | 'top';
    if (dropdownPosition === 'top') {
      direction = 'top';
    } else if (dropdownPosition === 'bottom') {
      direction = 'bottom';
    } else if (spaceBelow < 240 && rect.top > spaceBelow) {
      // 'auto': less than 240px below and more room above — drop up.
      direction = 'top';
    } else {
      direction = 'bottom';
    }

    // Cap the list height to the space actually available on the chosen
    // side of the viewport (still never above the max-h-56 cap), and keep
    // the list inside the viewport horizontally.
    const available = direction === 'bottom'
      ? vh - rect.bottom - MENU_GAP - VIEWPORT_MARGIN
      : rect.top - MENU_GAP - VIEWPORT_MARGIN;
    const maxHeight = Math.max(0, Math.min(MENU_MAX_HEIGHT, available));
    const width = Math.max(0, Math.min(rect.width, vw - VIEWPORT_MARGIN * 2));
    const left = Math.min(
      Math.max(rect.left, VIEWPORT_MARGIN),
      Math.max(VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - width),
    );

    return direction === 'bottom'
      ? { direction, top: rect.bottom + MENU_GAP, left, width, maxHeight }
      : { direction, bottom: vh - rect.top + MENU_GAP, left, width, maxHeight };
  }, [dropdownPosition]);

  const closeSoon = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    blurTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const handleOpen = () => {
    if (disabled) return;
    const pos = computeMenuPosition();
    if (pos) setMenuPos(pos);
    setOpen(true);
    setQuery('');
  };

  // While open: track the anchor. Recompute on resize and on any scroll
  // (capture phase reaches inner scroll containers, matching the
  // CannedReplyPicker precedent); close when the input has scrolled
  // fully out of view so the list never floats detached from its field.
  // A document mousedown outside BOTH the input wrapper and the
  // portalled list closes immediately (previously this only happened
  // indirectly through the input's blur timer); a mousedown inside the
  // list is recorded so the blur it causes is ignored (see handleBlur).
  useEffect(() => {
    if (!open) {
      menuPointerRef.current = false;
      return;
    }
    const sync = () => {
      const pos = computeMenuPosition();
      if (!pos) {
        setOpen(false);
        return;
      }
      setMenuPos(prev => (
        prev
        && prev.direction === pos.direction
        && prev.top === pos.top
        && prev.bottom === pos.bottom
        && prev.left === pos.left
        && prev.width === pos.width
        && prev.maxHeight === pos.maxHeight
      ) ? prev : pos);
    };
    sync();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      menuPointerRef.current = !!t && !!menuRef.current?.contains(t);
      if (!t) return;
      if (containerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      if (blurTimer.current) clearTimeout(blurTimer.current);
      setOpen(false);
    };
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, computeMenuPosition]);

  const handleBlur = () => {
    // A mousedown inside the portalled list blurs the input but is not
    // the user leaving the control — swallow that one blur.
    if (menuPointerRef.current) {
      menuPointerRef.current = false;
      return;
    }
    closeSoon();
  };

  const pick = (v: string) => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    onChange(v);
    setQuery('');
    setOpen(false);
  };

  const inputValue = open ? query : selected ? selected.label : '';

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          id={id}
          value={inputValue}
          placeholder={placeholder}
          aria-label={ariaLabel}
          disabled={disabled}
          onFocus={handleOpen}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onBlur={handleBlur}
          className="w-full pl-9 pr-8 py-2.5 text-base md:py-2 md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
        />
        {value && !disabled ? (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={e => { e.preventDefault(); pick(''); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded cursor-pointer"
            title="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">▾</span>
        )}
      </div>

      {open && !disabled && menuPos && createPortal(
        <div
          ref={menuRef}
          onWheel={(e) => e.stopPropagation()}
          className="fixed bg-white border border-slate-200 rounded-xl shadow-xl overflow-y-auto overscroll-contain"
          style={{
            top: menuPos.top,
            bottom: menuPos.bottom,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: menuPos.maxHeight,
            zIndex: menuZ,
          }}
        >
          {allowClear && (
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); pick(''); }}
              className="w-full text-left px-3 py-2 min-h-[44px] md:min-h-0 flex items-center text-xs text-slate-500 hover:bg-slate-50 cursor-pointer"
            >
              {clearLabel}
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">{emptyLabel}</div>
          ) : (
            filtered.map(o => (
              <button
                key={o.value}
                type="button"
                disabled={!!o.disabled}
                onMouseDown={e => { e.preventDefault(); if (!o.disabled) pick(o.value); }}
                className={`w-full text-left px-3 py-2 min-h-[44px] md:min-h-0 text-xs hover:bg-blue-50 flex items-center justify-between gap-2 ${o.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${o.value === value ? 'bg-blue-50/60' : ''}`}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-800">{o.label}</span>
                  {o.hint && <span className="block truncate text-[10px] text-slate-400">{o.hint}</span>}
                </span>
                {o.tag && (
                  <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 whitespace-nowrap">
                    {o.tag}
                  </span>
                )}
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
